import { useState, useEffect } from "react";
import axios from "axios";
import BrandNav from "@/components/brand/BrandNav";
import BrandHero from "@/components/brand/BrandHero";
import EditorialSections from "@/components/brand/EditorialSections";
import DeliveryPickupSection from "@/components/brand/DeliveryPickupSection";
import { FloatingCartBar } from "@/components/brand/FloatingCartBar";
import { scrollToMenu } from "@/components/brand/BrandAssets";
import MenuCatalog from "@/components/MenuCatalog";
import Footer from "@/components/Footer";
import Cart from "@/components/Cart";
import OrderModal from "@/components/OrderModal";
import GroupOrderModal from "@/components/GroupOrderModal";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  // Initialize language from localStorage or default to Latvian
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language') || localStorage.getItem('selectedLanguage');
    return savedLanguage || "lv"; // Default to Latvian
  });
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isGroupOrderModalOpen, setIsGroupOrderModalOpen] = useState(false);
  const [preferredDeliveryType, setPreferredDeliveryType] = useState("delivery");
  const [loading, setLoading] = useState(true);
  const [restaurantStatus, setRestaurantStatus] = useState(null);
  const [deliveryConfig, setDeliveryConfig] = useState({
    minimum_order_amount: 20,
    free_delivery_threshold: 35,
    delivery_fee: 2.5
  });

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
    localStorage.setItem('selectedLanguage', language);
  }, [language]);

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/seed`);
      
      // Fetch categories, menu, restaurant status, and delivery config
      const [categoriesRes, menuRes, statusRes, deliveryConfigRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/restaurant-status`),
        axios.get(`${API}/delivery-config`)
      ]);
      
      setCategories(categoriesRes.data);
      setMenuItems(menuRes.data);
      setRestaurantStatus(statusRes.data);
      setDeliveryConfig(deliveryConfigRes.data);
      setLoading(false);
      applyRepeatOrder(menuRes.data);
    } catch (error) {
      console.error("Error initializing data:", error);
      toast.error("Failed to load menu");
      setLoading(false);
    }
  };

  const ordersDisabled = restaurantStatus?.is_manually_closed === true;

  const applyRepeatOrder = (items) => {
    const raw = localStorage.getItem("rozedinapoli_repeat_order");
    if (!raw) return;
    localStorage.removeItem("rozedinapoli_repeat_order");
    let wanted = [];
    try { wanted = JSON.parse(raw); } catch (e) { return; }
    const restored = wanted
      .map((w) => {
        const item = items.find((m) => m.id === w.menu_item_id && m.available !== false);
        return item ? { ...item, quantity: w.quantity } : null;
      })
      .filter(Boolean);
    const missing = wanted.length - restored.length;
    if (restored.length === 0) {
      toast.error({ lv: "Šie ēdieni vairs nav pieejami", ru: "Эти блюда больше недоступны", en: "These items are no longer available" }[language]);
      return;
    }
    setCart(restored);
    setIsCartOpen(true);
    const msg = { lv: "Pasūtījums atjaunots grozā", ru: "Заказ восстановлен в корзине", en: "Order restored to cart" }[language];
    const note = missing > 0 ? { lv: ` (${missing} nav pieejami)`, ru: ` (${missing} недоступно)`, en: ` (${missing} unavailable)` }[language] : "";
    toast.success(msg + note);
  };

  const addToCart = (item) => {
    if (ordersDisabled) {
      toast.info(
        language === 'ru'
          ? 'Онлайн-заказы временно недоступны. Отведайте наши пиццы в ресторане Roze (restoransroze.lv).'
          : language === 'lv'
          ? 'Tiešsaistes pasūtījumi šobrīd nav pieejami. Baudiet mūsu pizzas restorānā Roze (restoransroze.lv).'
          : 'Online orders are currently paused. Enjoy our pizzas at Restaurant Roze (restoransroze.lv).'
      );
      return;
    }
    if (item.price == null) return;
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prevCart, { ...item, quantity: 1 }];
    });
    toast.success(getTranslation("itemAdded", language));
  };

  const removeFromCart = (itemId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const filteredMenuItems = selectedCategory
    ? menuItems.filter(item => item.category === selectedCategory)
    : (() => {
        // Sort items by category order when showing all
        const categoryOrder = categories.reduce((acc, cat, index) => {
          acc[cat.slug] = index;
          return acc;
        }, {});
        return [...menuItems].sort((a, b) => {
          const orderA = categoryOrder[a.category] ?? 999;
          const orderB = categoryOrder[b.category] ?? 999;
          return orderA - orderB;
        });
      })();

  const statusText = restaurantStatus && (() => {
    const dayTranslations = {
      'Monday': { en: 'Monday', ru: 'Понедельник', lv: 'Pirmdiena' },
      'Tuesday': { en: 'Tuesday', ru: 'Вторник', lv: 'Otrdiena' },
      'Wednesday': { en: 'Wednesday', ru: 'Среда', lv: 'Trešdiena' },
      'Thursday': { en: 'Thursday', ru: 'Четверг', lv: 'Ceturtdiena' },
      'Friday': { en: 'Friday', ru: 'Пятница', lv: 'Piektdiena' },
      'Saturday': { en: 'Saturday', ru: 'Суббота', lv: 'Sestdiena' },
      'Sunday': { en: 'Sunday', ru: 'Воскресенье', lv: 'Svētdiena' }
    };
    const day = dayTranslations[restaurantStatus.current_day]?.[language] || restaurantStatus.current_day;
    const no = restaurantStatus.next_open;
    const nextDay = no && (no.is_today ? { en: "today", ru: "сегодня", lv: "šodien" }[language] : no.is_tomorrow ? { en: "tomorrow", ru: "завтра", lv: "rīt" }[language] : dayTranslations[no.day]?.[language]);
    const preorder = no ? { en: ` · Pre-order for ${nextDay} from ${no.time}`, ru: ` · Предзаказ на ${nextDay} с ${no.time}`, lv: ` · Iepriekšējs pasūtījums uz ${nextDay} no ${no.time}` }[language] || "" : "";
    const isHoliday = restaurantStatus.open_time === "Closed" && restaurantStatus.close_time === "Closed";
    const hours = `${restaurantStatus.open_time}–${restaurantStatus.close_time}`;
    const text = {
      en: restaurantStatus.is_open ? `Open · ${day} ${hours}` : (restaurantStatus.is_day_off ? `Closed on ${day}s` : isHoliday ? `Closed today (holiday)` : `Closed · ${day} ${hours}`) + preorder,
      ru: restaurantStatus.is_open ? `Открыто · ${day} ${hours}` : (restaurantStatus.is_day_off ? `${day} — выходной` : isHoliday ? `Сегодня закрыто (праздник)` : `Закрыто · ${day} ${hours}`) + preorder,
      lv: restaurantStatus.is_open ? `Atvērts · ${day} ${hours}` : (restaurantStatus.is_day_off ? `${day} — slēgts` : isHoliday ? `Šodien slēgts (svētki)` : `Slēgts · ${day} ${hours}`) + preorder,
    };
    return text[language] || text.lv;
  })();

  return (
    <div className="min-h-screen rz-page" id="top">
      <BrandNav
        language={language}
        setLanguage={setLanguage}
        cartItemsCount={cart.reduce((sum, item) => sum + item.quantity, 0)}
        onCartClick={() => setIsCartOpen(true)}
        onGroupOrderClick={() => setIsGroupOrderModalOpen(true)}
      />

      <BrandHero language={language} isOpen={restaurantStatus?.is_open === true} statusText={statusText} />

      <EditorialSections language={language} />

      <DeliveryPickupSection
        language={language}
        deliveryConfig={deliveryConfig}
        restaurantStatus={restaurantStatus}
        onChoose={(type) => { setPreferredDeliveryType(type); scrollToMenu(); }}
      />
      
      <MenuCatalog
        language={language}
        categories={categories}
        menuItems={filteredMenuItems}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        addToCart={addToCart}
        loading={loading}
        ordersDisabled={ordersDisabled}
      />
      
      <Footer language={language} />

      <FloatingCartBar
        language={language}
        count={cart.reduce((sum, item) => sum + item.quantity, 0)}
        total={getCartTotal()}
        onClick={() => setIsCartOpen(true)}
      />
      
      <Cart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        language={language}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        getCartTotal={getCartTotal}
        deliveryConfig={deliveryConfig}
        onCheckout={() => {
          if (ordersDisabled) return;
          setIsCartOpen(false);
          setIsOrderModalOpen(true);
        }}
        ordersDisabled={ordersDisabled}
      />
      
      <OrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        cart={cart}
        language={language}
        getCartTotal={getCartTotal}
        deliveryConfig={deliveryConfig}
        onOrderSuccess={() => {
          setCart([]);
          setIsOrderModalOpen(false);
        }}
        cardPaymentsEnabled={restaurantStatus?.card_payments_enabled === true}
        initialDeliveryType={preferredDeliveryType}
        restaurantOpen={restaurantStatus?.is_open !== false}
      />
      
      <GroupOrderModal
        isOpen={isGroupOrderModalOpen}
        onClose={() => setIsGroupOrderModalOpen(false)}
        language={language}
      />
    </div>
  );
};

const getTranslation = (key, language) => {
  const translations = {
    itemAdded: {
      en: "Item added to cart",
      ru: "Товар добавлен в корзину",
      lv: "Prece pievienota grozam"
    }
  };
  return translations[key]?.[language] || translations[key]?.en;
};

export default HomePage;